import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["toggle", "menu", "item"]
  static values = {
    processingText: String,
    disabled: Boolean
  }

  connect() {
    this.originalToggleText = this.toggleTarget.innerHTML
    this.isProcessing = false
    this.isOpen = false

    // Set default processing text if not provided
    if (!this.processingTextValue) {
      this.processingTextValue = "Processing..."
    }

    // Bind the outside click handler to maintain context
    this.handleOutsideClick = this.handleOutsideClick.bind(this)

    // Initialize disabled state if specified
    if (this.disabledValue) {
      this.disable()
    }
  }

  disconnect() {
    // Clean up event listener
    document.removeEventListener('click', this.handleOutsideClick)
  }

  // Toggle dropdown open/close
  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isProcessing || this.toggleTarget.disabled) {
      return
    }

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  // Open dropdown
  open() {
    if (this.isProcessing || this.toggleTarget.disabled) {
      return
    }

    this.isOpen = true
    this.element.classList.add('open')
    this.menuTarget.classList.add('open')
    this.toggleTarget.setAttribute('aria-expanded', 'true')

    // Add outside click listener
    setTimeout(() => {
      document.addEventListener('click', this.handleOutsideClick)
    }, 0)
  }

  // Close dropdown
  close() {
    this.isOpen = false
    this.element.classList.remove('open')
    this.menuTarget.classList.remove('open')
    this.toggleTarget.setAttribute('aria-expanded', 'false')

    // Remove outside click listener
    document.removeEventListener('click', this.handleOutsideClick)
  }

  // Handle clicks outside the dropdown
  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  // Handle item clicks (prevents double-clicks)
  itemClicked(event) {
    // Prevent double-clicks
    if (this.isProcessing) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }

    // Mark as processing
    this.startProcessing()

    // Allow the original action to proceed
    return true
  }

  // Start processing state
  startProcessing() {
    this.isProcessing = true

    // Update toggle button
    this.toggleTarget.disabled = true
    this.toggleTarget.innerHTML = `
      ${this.processingTextValue}
      <span class="caret"></span>
    `
    this.toggleTarget.classList.add('tw-cursor-not-allowed')

    // Disable all dropdown items
    this.itemTargets.forEach(item => {
      item.style.pointerEvents = 'none'
      item.classList.add('tw-opacity-50', 'tw-cursor-not-allowed')
    })

    // Close the dropdown
    this.close()
  }

  // Reset to initial state
  reset() {
    this.isProcessing = false
    this.toggleTarget.disabled = false
    this.toggleTarget.innerHTML = this.originalToggleText
    this.toggleTarget.classList.remove('tw-cursor-not-allowed')

    this.itemTargets.forEach(item => {
      item.style.pointerEvents = 'auto'
      item.classList.remove('tw-opacity-50', 'tw-cursor-not-allowed')
    })
  }

  // Disable the entire dropdown
  disable() {
    this.toggleTarget.disabled = true
    this.toggleTarget.classList.add('tw-cursor-not-allowed')
    this.close()
  }

  // Enable the dropdown
  enable() {
    if (!this.isProcessing) {
      this.toggleTarget.disabled = false
      this.toggleTarget.classList.remove('tw-cursor-not-allowed')
    }
  }

  // Handle keyboard navigation (accessibility)
  keydown(event) {
    if (!this.isOpen) return

    switch(event.key) {
      case 'Escape':
        event.preventDefault()
        this.close()
        this.toggleTarget.focus()
        break
      case 'ArrowDown':
        event.preventDefault()
        this.focusNext()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.focusPrevious()
        break
    }
  }

  // Focus next item (for keyboard navigation)
  focusNext() {
    const focusableItems = this.itemTargets.filter(item =>
      item.style.pointerEvents !== 'none' && !item.disabled
    )
    const currentIndex = focusableItems.indexOf(document.activeElement.closest('[data-bootstrap-styles-dropdown-target="item"]'))
    const nextIndex = (currentIndex + 1) % focusableItems.length
    focusableItems[nextIndex]?.focus()
  }

  // Focus previous item (for keyboard navigation)
  focusPrevious() {
    const focusableItems = this.itemTargets.filter(item =>
      item.style.pointerEvents !== 'none' && !item.disabled
    )
    const currentIndex = focusableItems.indexOf(document.activeElement.closest('[data-bootstrap-styles-dropdown-target="item"]'))
    const previousIndex = currentIndex <= 0 ? focusableItems.length - 1 : currentIndex - 1
    focusableItems[previousIndex]?.focus()
  }
}
